/**
 * Чистая логика экрана «Изменить трек» (`/edit-track`, он же `/extend-song`):
 * разбор времени, выбор режима и валидация отрезка «Заменить секцию».
 *
 * Вынесено из React-страниц отдельным модулем (без DOM и сети) — его гоняют
 * юнит-тесты, см. editTrack.test.ts.
 */
/**
 * Парсит ввод времени: "1:52" или "112" → секунды. Возвращает null для
 * невалидного ввода (пустое, буквы, секунды ≥ 60 в формате мм:сс).
 */
export function parseTimeToSeconds(raw) {
    const s = raw.trim();
    if (!s)
        return null;
    if (s.includes(":")) {
        const parts = s.split(":");
        if (parts.length !== 2)
            return null;
        const [mm, ss] = parts;
        if (!/^\d+$/.test(mm) || !/^\d+$/.test(ss))
            return null;
        const seconds = Number(ss);
        if (seconds >= 60)
            return null;
        return Number(mm) * 60 + seconds;
    }
    if (!/^\d+$/.test(s))
        return null;
    return Number(s);
}
/** Секунды → "м:сс" для подписей и полей ввода. */
export function formatSeconds(total) {
    const t = Math.max(0, total);
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}
/**
 * Режим из `?mode=`. По умолчанию — продление: старые ссылки (`/extend-song`,
 * кнопки ботов) должны вести туда же, куда вели раньше.
 */
export function parseEditMode(raw) {
    return raw === "replace" ? "replace" : "extend";
}
/**
 * Замена секции — только для песни (normal/demo). Стемы (минус/вокал) бэк
 * отклоняет (409 replace_section_not_supported_for_stem); неизвестный тип —
 * не гадаем и НЕ предлагаем (пункт fallback-меню не добавляется).
 */
export function replaceSupportedForAudioType(type) {
    return type === "normal" || type === "demo";
}
/**
 * Какие режимы разрешены треку. Есть `available_actions` — решают они. Набора
 * нет (ссылка из бота, лёгкие источники плеера): продление разрешаем, замену —
 * если тип неизвестен (решит бэк, 409 покажем понятным текстом) или это песня;
 * для стема — нет.
 */
export function editModeAvailability(actions, audioType) {
    if (!actions) {
        return {
            extend: true,
            replace: audioType == null || replaceSupportedForAudioType(audioType),
        };
    }
    return {
        extend: actions.includes("extend"),
        replace: actions.includes("replace_section"),
    };
}
/**
 * Запрошенный режим, если он разрешён; иначе — другой разрешённый. Если не
 * разрешено ничего, оставляем запрошенный (карточки будут задизейблены).
 */
export function resolveEditMode(requested, availability) {
    if (availability[requested])
        return requested;
    const other = requested === "extend" ? "replace" : "extend";
    return availability[other] ? other : requested;
}
// ── Отрезок «Заменить секцию» ────────────────────────────────────────────────
/** Ограничения Suno: отрезок не короче 10 секунд… */
export const REPLACE_MIN_SECONDS = 10;
/** …и не длиннее половины трека. */
export const REPLACE_MAX_RATIO = 0.5;
/**
 * Запас к «≤50%»: длину трека на фронте мы знаем из декода/метаданных с
 * дробной частью, а воркер меряет свою. Считаем от floor(длительности) и
 * отнимаем полсекунды — иначе отрезок «ровно на границе» проходит здесь и
 * падает в воркере (FAILED + refund), когда деньги уже списаны.
 */
export const REPLACE_MAX_MARGIN_SECONDS = 0.5;
/** Максимальная длина отрезка для трека длительностью `duration` (сек). */
export function replaceMaxLength(duration) {
    return Math.floor(duration) * REPLACE_MAX_RATIO - REPLACE_MAX_MARGIN_SECONDS;
}
/**
 * Проверка отрезка [start; end] (секунды) до сабмита — те же правила, что у
 * бэка (`replace_section_too_short|too_long|out_of_range`), чтобы человек
 * не упирался в 422 после клика.
 *
 * `duration` неизвестна (аудио ещё грузится / не удалось прочитать) — не
 * отправляем вслепую: «≤50%» бэк проверяет только в воркере, уже после
 * списания, поэтому сабмит блокируется до получения длительности.
 */
export function validateReplaceRange(start, end, duration) {
    if (start == null ||
        end == null ||
        !Number.isFinite(start) ||
        !Number.isFinite(end) ||
        start < 0) {
        return { ok: false, error: "invalid" };
    }
    if (end <= start)
        return { ok: false, error: "order" };
    const length = end - start;
    if (length < REPLACE_MIN_SECONDS)
        return { ok: false, error: "too_short" };
    if (duration == null || !Number.isFinite(duration) || duration <= 0) {
        return { ok: false, error: "duration_unknown" };
    }
    if (end > Math.floor(duration)) {
        return { ok: false, error: "out_of_range" };
    }
    if (length > replaceMaxLength(duration)) {
        return { ok: false, error: "too_long" };
    }
    return { ok: true };
}
/**
 * Стартовый отрезок, когда стала известна длительность: примерно середина
 * трека, длиной ~15% (но в рамках 10 с … 50% трека) — сразу валидный и
 * видимый на волне. Для трека короче 20 с валидного отрезка не существует —
 * возвращаем null, форма покажет ошибку.
 */
export function defaultReplaceRange(duration) {
    if (!Number.isFinite(duration) || duration <= 0)
        return null;
    const maxLen = Math.floor(replaceMaxLength(duration));
    if (maxLen < REPLACE_MIN_SECONDS)
        return null;
    const len = Math.min(maxLen, Math.max(REPLACE_MIN_SECONDS, Math.round(duration * 0.15)));
    const start = Math.max(0, Math.round(duration * 0.4));
    const end = Math.min(Math.floor(duration), start + len);
    return { start: end - len, end };
}
function detailText(detail) {
    if (typeof detail === "string")
        return detail;
    if (detail != null && typeof detail === "object") {
        try {
            return JSON.stringify(detail);
        }
        catch {
            return "";
        }
    }
    return "";
}
/**
 * Код причины (из `detail` ответа, `reason` уведомления/WS) → причина замены.
 * Не наш код (например, внутренний код Suno) → null — покажем общий текст.
 */
export function replaceSectionReason(reason) {
    const text = detailText(reason);
    if (text.includes("replace_section_too_short"))
        return "too_short";
    if (text.includes("replace_section_too_long"))
        return "too_long";
    if (text.includes("replace_section_out_of_range"))
        return "out_of_range";
    if (text.includes("replace_section_not_supported_for_stem"))
        return "stem";
    if (text.includes("insufficient_balance"))
        return "insufficient_balance";
    return null;
}
/**
 * Ошибка ответа POST replace-section → причина. 409 со стем-кодом и 400 (у
 * трека нет задачи Suno — замену сделать не из чего) бэк отдаёт синхронно.
 */
export function replaceSectionRequestError(status, detail) {
    const byCode = replaceSectionReason(detail);
    if (byCode)
        return byCode;
    if (status === 400)
        return "no_source_task";
    return null;
}
/**
 * Человекочитаемая причина сбоя замены — для тоста WS и центра уведомлений
 * (там длительности трека нет, поэтому без цифр). null — причина неизвестна.
 */
export function replaceSectionFailureText(failure, ru) {
    switch (failure) {
        case "too_short":
            return ru
                ? "Отрезок короче 10 секунд."
                : "The section is shorter than 10 seconds.";
        case "too_long":
            return ru
                ? "Отрезок длиннее половины песни — выберите покороче."
                : "The section is longer than half the song — pick a shorter one.";
        case "out_of_range":
            return ru
                ? "Отрезок выходит за пределы песни."
                : "The section is outside the song.";
        case "insufficient_balance":
            return ru
                ? "Не хватило песен на балансе."
                : "Not enough songs on your balance.";
        case "stem":
            return ru
                ? "Замена части недоступна для минуса и вокала — откройте саму песню."
                : "Section replacement isn't available for instrumentals and vocals — open the song itself.";
        case "no_source_task":
            return ru
                ? "Для этого трека замена недоступна — попробуйте на более новой версии."
                : "Replacement isn't available for this track — try a newer version.";
        default:
            return null;
    }
}
// ── Производные треки и маркеры «генерируется» ──────────────────────────────
/**
 * Чем строка является по отношению к исходному треку: продолжением
 * (extend) или версией с заменённой частью. У обоих есть `source_audio_id`,
 * различает их `replace_start_s` (есть только у замены).
 */
export function derivedTrackKind(t) {
    if (t.replaceStartS != null)
        return "replaced";
    if (t.sourceAudioId != null)
        return "continuation";
    return null;
}
/** Id исходного аудио из WS-события замены: `object_id`, запасной — `data.audio_id`. */
export function replaceEventAudioId(payload) {
    const pick = (v) => typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null;
    return pick(payload.object_id) ?? pick(payload.data?.audio_id);
}
/**
 * Сразу после своего заказа GET статуса может ещё отдать прошлый READY/FAILED
 * (прошлая замена этого же аудио) — терминальные ответы поллинга первые
 * 60 секунд игнорируем.
 */
export const REPLACE_POLL_GRACE_MS = 60 * 1000;
export function shouldApplyReplacePollResult(startedAt, now) {
    return startedAt == null || now - startedAt >= REPLACE_POLL_GRACE_MS;
}
/**
 * Что отправить в `full_lyrics`: только если человек изменил текст
 * относительно исходного (иначе null — бэк оставит прежний). Пустое — null.
 */
export function fullLyricsToSend(current, original) {
    const c = current.trim();
    if (!c)
        return null;
    return c === original.trim() ? null : c;
}
